import { useMemo, useState } from 'react';
import { toast } from 'sonner';
import { MegaphoneIcon } from 'lucide-react';
import {
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Textarea,
} from '@tinct/web-components';
import { useGenerateAdPage, useGetAdsCampaigns } from '@tinct/core-api';
import type { AdsProviderType, ReadAdsCampaign } from '@tinct/core-api-models';
import { useGetCurrentQuotas } from '@tinct/core-workspace';
import { useOpenPaywall } from '@tinct/web-feature-payment';
import { ADS_PROVIDER_LABELS, AdsProviderIcon } from '../../shared';

type GenerateAdPageDialogProps = {
  campaignId: string;
  /** The workspace's READY ads integrations (dialog only opens when one exists). */
  adsIntegrations: { id: string; providerType: AdsProviderType }[];
  open: boolean;
  onOpenChange: (open: boolean) => void;
};

type AdsCampaignOption = ReadAdsCampaign & { providerType: AdsProviderType };

/**
 * "Generate from ad campaign": pick a synced ads campaign (Google Ads or
 * LinkedIn Ads — matched ones for this campaign first) + an optional prompt,
 * then start a page generation targeting it. Quota-gated like every other
 * generation: on a short quota the paywall opens and the dialog deliberately
 * stays open.
 */
export function GenerateAdPageDialog({
  campaignId,
  adsIntegrations,
  open,
  onOpenChange,
}: GenerateAdPageDialogProps) {
  const [selectedAdsCampaignId, setSelectedAdsCampaignId] = useState<string>('');
  const [userPrompt, setUserPrompt] = useState('');

  const { lpGenerationQuota } = useGetCurrentQuotas();
  const { openPaywall } = useOpenPaywall();

  // One list per provider (at most one integration each), merged below.
  const googleAdsIntegrationId = adsIntegrations.find((i) => i.providerType === 'google_ads')?.id;
  const linkedinAdsIntegrationId = adsIntegrations.find((i) => i.providerType === 'linkedin_ads')?.id;
  const { data: googleAdsCampaigns } = useGetAdsCampaigns(
    { integrationId: googleAdsIntegrationId ?? '', offset: 0, limit: 100 },
    { enabled: open && !!googleAdsIntegrationId },
  );
  const { data: linkedinAdsCampaigns } = useGetAdsCampaigns(
    { integrationId: linkedinAdsIntegrationId ?? '', offset: 0, limit: 100 },
    { enabled: open && !!linkedinAdsIntegrationId },
  );

  // Matched-to-this-campaign rows first — they're the natural candidates.
  const options = useMemo<AdsCampaignOption[]>(() => {
    const rows: AdsCampaignOption[] = [
      ...(googleAdsCampaigns?.data ?? []).map((row) => ({ ...row, providerType: 'google_ads' as const })),
      ...(linkedinAdsCampaigns?.data ?? []).map((row) => ({ ...row, providerType: 'linkedin_ads' as const })),
    ];
    return rows.sort((a, b) => {
      const aMatched = a.matchedCampaignId === campaignId ? 0 : 1;
      const bMatched = b.matchedCampaignId === campaignId ? 0 : 1;
      return aMatched - bMatched;
    });
  }, [googleAdsCampaigns, linkedinAdsCampaigns, campaignId]);
  const showProvider = adsIntegrations.length > 1;

  const generateAdPage = useGenerateAdPage({
    onSuccess: () => {
      toast.success('Ad page generation started');
      onOpenChange(false);
      setSelectedAdsCampaignId('');
      setUserPrompt('');
    },
    onError: (error) => toast.error(error.error),
  });

  const handleGenerate = () => {
    if (!selectedAdsCampaignId) return;
    // One new page = one credit. `pageNeedsGeneration` counting doesn't apply
    // here (the page doesn't exist yet).
    if ((lpGenerationQuota?.remainingQuota ?? 0) < 1) {
      openPaywall('not_enough_credits');
      return; // dialog stays open — same convention as generate-all
    }
    generateAdPage.mutate({
      campaignId,
      adsCampaignId: selectedAdsCampaignId,
      userPrompt: userPrompt.trim() || undefined,
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Generate from ad campaign</DialogTitle>
        </DialogHeader>

        <div className="flex flex-col gap-4">
          <p className="text-sm text-muted-foreground">
            Visitors clicking this ads campaign (Google Ads / LinkedIn Ads)
            will land on their own page variant instead of the generic page.
          </p>

          <div className="flex flex-col gap-2">
            <Label>Ads campaign</Label>
            <Select
              value={selectedAdsCampaignId}
              onValueChange={setSelectedAdsCampaignId}
            >
              <SelectTrigger className="w-full">
                <SelectValue placeholder="Pick a synced ads campaign" />
              </SelectTrigger>
              <SelectContent>
                {options.map((row) =>
                  row.id ? (
                    <SelectItem key={row.id} value={row.id}>
                      <span className="flex items-center gap-2">
                        <AdsProviderIcon providerType={row.providerType} size={14} />
                        <span>
                          {row.name ?? row.externalCampaignId ?? row.id}
                          {showProvider ? ` · ${ADS_PROVIDER_LABELS[row.providerType]}` : ''}
                          {row.matchedCampaignId === campaignId ? ' · matched' : ''}
                        </span>
                      </span>
                    </SelectItem>
                  ) : null,
                )}
              </SelectContent>
            </Select>
            {options.length === 0 && (
              <p className="text-xs text-muted-foreground">
                No synced ads campaigns yet — run a sync from the Google Ads
                or LinkedIn Ads integration page first.
              </p>
            )}
          </div>

          <div className="flex flex-col gap-2">
            <Label htmlFor="ad-page-prompt">Instructions (optional)</Label>
            <Textarea
              id="ad-page-prompt"
              value={userPrompt}
              onChange={(e) => setUserPrompt(e.target.value)}
              placeholder="Anything the generated page should emphasize…"
              className="min-h-20"
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            onClick={handleGenerate}
            disabled={!selectedAdsCampaignId}
            isLoading={generateAdPage.isPending}
          >
            <MegaphoneIcon className="size-4" />
            Generate
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
